import { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Tabs, Tab, ToggleButtonGroup, ToggleButton,
  Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell, Link, Chip,
  Button, IconButton, Dialog, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { PmCard, PmInfoBox } from '../components/pm';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useNotifications } from '../context/NotificationContext';
import {
  WK_LIBERATING_STRUCTURES, WK_PIP_DECKS, WK_ORG_FORMEN, type WerkzeugLink,
} from '../data/werkzeugeData';
import { AppDialogTitle } from '../components/common/AppDialogTitle';

interface OwnMethod {
  id: string;
  name: string;
  desc: string;
  url: string;
}

const OWN_STORAGE = 'pm:werkzeuge-own';

function readOwn(): OwnMethod[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(OWN_STORAGE);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OwnMethod[];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeOwn(items: OwnMethod[]): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(OWN_STORAGE, JSON.stringify(items)); } catch { /* quota */ }
}

type TabKey = 'ls' | 'pip' | 'org' | 'own';
type ViewMode = 'cards' | 'table';

const VIEW_STORAGE = 'pm:werkzeuge-view';

function readView(): ViewMode {
  if (typeof window === 'undefined') return 'cards';
  return (window.localStorage.getItem(VIEW_STORAGE) as ViewMode) === 'table' ? 'table' : 'cards';
}

interface LinkListProps {
  items: WerkzeugLink[];
  view: ViewMode;
  search: string;
  /** Optional Edit/Delete-Handler — wenn gesetzt, werden Action-Icons gerendert (für „Eigene Methoden") */
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function matchesLink(it: WerkzeugLink, q: string): boolean {
  if (!q) return true;
  return it.name.toLowerCase().includes(q)
    || it.desc.toLowerCase().includes(q)
    || it.url.toLowerCase().includes(q);
}

function matchesOrg(f: typeof WK_ORG_FORMEN[number], q: string): boolean {
  if (!q) return true;
  return f.name.toLowerCase().includes(q)
    || f.anwendung.toLowerCase().includes(q)
    || f.nachteil.toLowerCase().includes(q)
    || f.plMacht.toLowerCase().includes(q)
    || f.flexibilitaet.toLowerCase().includes(q)
    || f.ressourcen.toLowerCase().includes(q)
    || f.verantwortung.toLowerCase().includes(q)
    || f.overhead.toLowerCase().includes(q);
}

function LinkList({ items, view, search, onEdit, onDelete }: LinkListProps) {
  const q = search.trim().toLowerCase();
  const filtered = items.filter((it) => matchesLink(it, q));

  if (filtered.length === 0) {
    return <PmInfoBox variant="info">Keine Treffer für „{search}".</PmInfoBox>;
  }

  const editable = !!onEdit || !!onDelete;
  const itemKey = (it: WerkzeugLink): string => ((it as unknown as { id?: string }).id ?? it.name);

  if (view === 'table') {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Methode</TableCell>
            <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Beschreibung</TableCell>
            <TableCell sx={{ color: 'var(--pm-fg-subtle)' }} align="right">Quelle</TableCell>
            {editable && <TableCell sx={{ color: 'var(--pm-fg-subtle)' }} align="right">Aktionen</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((it) => (
            <TableRow key={itemKey(it)} hover>
              <TableCell sx={{ fontWeight: 600 }}>{it.name}</TableCell>
              <TableCell sx={{ color: 'var(--pm-fg-muted)' }}>{it.desc}</TableCell>
              <TableCell align="right">
                {it.url ? (
                  <Link href={it.url} target="_blank" rel="noopener" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    Öffnen <OpenInNewIcon fontSize="inherit" />
                  </Link>
                ) : '—'}
              </TableCell>
              {editable && (
                <TableCell align="right">
                  {onEdit && (
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => onEdit(itemKey(it))} sx={{ color: 'var(--pm-fg-subtle)' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Löschen">
                      <IconButton size="small" onClick={() => onDelete(itemKey(it))} sx={{ color: 'var(--pm-error)' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
      {filtered.map((it) => (
        <Card key={itemKey(it)} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{it.name}</Typography>
              {editable && (
                <Stack direction="row" spacing={0}>
                  {onEdit && (
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => onEdit(itemKey(it))} sx={{ color: 'var(--pm-fg-subtle)', p: 0.25 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Löschen">
                      <IconButton size="small" onClick={() => onDelete(itemKey(it))} sx={{ color: 'var(--pm-error)', p: 0.25 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', flex: 1 }}>{it.desc}</Typography>
            {it.url ? (
              <Link href={it.url} target="_blank" rel="noopener" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                Öffnen <OpenInNewIcon fontSize="inherit" />
              </Link>
            ) : (
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontStyle: 'italic' }}>Kein Link hinterlegt</Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

export function WerkzeugePage() {
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const [tab, setTab] = useState<TabKey>('ls');
  const [view, setView] = useState<ViewMode>(() => readView());
  const [search, setSearch] = useState('');

  // Eigene Methoden (LocalStorage)
  const [ownMethods, setOwnMethods] = useState<OwnMethod[]>(() => readOwn());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; desc: string; url: string }>({ name: '', desc: '', url: '' });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    try { window.localStorage.setItem(VIEW_STORAGE, view); } catch { /* quota */ }
  }, [view]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', desc: '', url: '' });
    setDialogOpen(true);
  };

  const openEdit = (id: string) => {
    const item = ownMethods.find((m) => m.id === id);
    if (!item) return;
    setEditId(id);
    setForm({ name: item.name, desc: item.desc, url: item.url });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      notify('Titel ist Pflicht', 'warning');
      return;
    }
    const next = editId
      ? ownMethods.map((m) => (m.id === editId ? { ...m, name: form.name.trim(), desc: form.desc.trim(), url: form.url.trim() } : m))
      : [...ownMethods, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: form.name.trim(), desc: form.desc.trim(), url: form.url.trim() }];
    writeOwn(next);
    setOwnMethods(next);
    setDialogOpen(false);
    notify(tc('status.saved', 'Gespeichert'), 'success');
  };

  const handleDelete = () => {
    if (!deleteId) return;
    const next = ownMethods.filter((m) => m.id !== deleteId);
    writeOwn(next);
    setOwnMethods(next);
    setDeleteId(null);
    notify(tc('status.deleted', 'Gelöscht'), 'success');
  };

  // Global-Search: zähle Treffer pro Tab, damit Labels die Anzahl zeigen können
  const searchActive = search.trim().length > 0;
  const counts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return {
      ls: WK_LIBERATING_STRUCTURES.filter((it) => matchesLink(it, q)).length,
      pip: WK_PIP_DECKS.filter((it) => matchesLink(it, q)).length,
      org: WK_ORG_FORMEN.filter((f) => matchesOrg(f, q)).length,
      own: ownMethods.filter((m) => matchesLink(m, q)).length,
    };
  }, [search, ownMethods]);
  const totalMatches = counts.ls + counts.pip + counts.org + counts.own;

  // Auto-Switch: aktiver Tab leer, aber andere Tabs haben Treffer → springe zum ersten Tab mit Treffer.
  // Damit der User beim Tippen nicht im leeren Tab "hängenbleibt".
  useEffect(() => {
    if (!searchActive || counts[tab] > 0 || totalMatches === 0) return;
    const order: TabKey[] = ['ls', 'pip', 'org', 'own'];
    const target = order.find((k) => counts[k] > 0);
    if (target && target !== tab) setTab(target);
  }, [searchActive, counts, totalMatches, tab]);

  const tabContent = useMemo(() => {
    switch (tab) {
      case 'ls':
        return (
          <Stack spacing={1.5}>
            <PmInfoBox variant="info" title="Liberating Structures">
              33+ Mikrostrukturen für besseres Zusammenarbeiten. Externe Quelle:{' '}
              <Link href="https://liberatingstructures.de/liberating-structures-menue/alle-liberating-structures-alphabetisch-sortiert/" target="_blank" rel="noopener" underline="hover">
                liberatingstructures.de
              </Link>
            </PmInfoBox>
            <LinkList items={WK_LIBERATING_STRUCTURES} view={view} search={search} />
          </Stack>
        );
      case 'pip':
        return (
          <Stack spacing={1.5}>
            <PmInfoBox variant="info" title="PIP DECKS">
              Karten-Decks zu Workshop-Tactics, Storytelling, Teamwork, Innovation. Externe Quelle:{' '}
              <Link href="https://pipdecks.com" target="_blank" rel="noopener" underline="hover">
                pipdecks.com
              </Link>
            </PmInfoBox>
            <LinkList items={WK_PIP_DECKS} view={view} search={search} />
          </Stack>
        );
      case 'org': {
        const q = search.trim().toLowerCase();
        const filtered = WK_ORG_FORMEN.filter((f) => matchesOrg(f, q));
        return (
          <Stack spacing={1.5}>
            <PmInfoBox variant="info" title="Projektorganisationsformen">
              Auswahl-Hilfe: welche Organisationsform passt zu welchem Projekt?
            </PmInfoBox>
            {view === 'table' ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Organisationsform</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>PL-Macht</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Flexibilität</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Ressourceneffizienz</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Klare Verantwortung</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Overhead</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Beste Anwendung</TableCell>
                      <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Hauptnachteil</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((f) => (
                      <TableRow key={f.name} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{f.name}</TableCell>
                        <TableCell>{f.plMacht}</TableCell>
                        <TableCell>{f.flexibilitaet}</TableCell>
                        <TableCell>{f.ressourcen}</TableCell>
                        <TableCell>{f.verantwortung}</TableCell>
                        <TableCell>{f.overhead}</TableCell>
                        <TableCell sx={{ color: 'var(--pm-fg-muted)' }}>{f.anwendung}</TableCell>
                        <TableCell sx={{ color: 'var(--pm-fg-muted)' }}>{f.nachteil}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
                {filtered.map((f) => (
                  <Card key={f.name} variant="outlined">
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{f.name}</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={`PL-Macht: ${f.plMacht}`} />
                        <Chip size="small" variant="outlined" label={`Flexibilität: ${f.flexibilitaet}`} />
                        <Chip size="small" variant="outlined" label={`Verantwortung: ${f.verantwortung}`} />
                        <Chip size="small" variant="outlined" label={`Overhead: ${f.overhead}`} />
                      </Stack>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block' }}>Beste Anwendung</Typography>
                        <Typography variant="body2">{f.anwendung}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block' }}>Hauptnachteil</Typography>
                        <Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)' }}>{f.nachteil}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Stack>
        );
      }
      case 'own': {
        const q = search.trim().toLowerCase();
        const filtered = ownMethods.filter((m) => matchesLink(m, q));
        return (
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <PmInfoBox variant="info" title="Eigene Methoden">
                Hier kannst du eigene Moderationswerkzeuge, Methoden oder Tools mit Link erfassen. Speicherung erfolgt aktuell lokal im Browser.
              </PmInfoBox>
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                Neue Methode
              </Button>
            </Stack>
            {ownMethods.length === 0 ? (
              <PmInfoBox variant="info">
                Noch keine eigenen Methoden hinterlegt. Klick auf „Neue Methode", um den ersten Eintrag anzulegen.
              </PmInfoBox>
            ) : filtered.length === 0 ? (
              <PmInfoBox variant="info">Keine Treffer für „{search}".</PmInfoBox>
            ) : (
              <LinkList
                items={filtered.map((m) => ({ name: m.name, desc: m.desc, url: m.url, ...({ id: m.id } as object) }))}
                view={view}
                search=""
                onEdit={openEdit}
                onDelete={(id) => setDeleteId(id)}
              />
            )}
          </Stack>
        );
      }
    }
  }, [tab, view, search, ownMethods]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <BuildOutlinedIcon sx={{ fontSize: 28, color: 'var(--pm-fg-accent)' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Werkzeugkoffer</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            Moderationswerkzeuge, Methoden und Organisationsformen
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="cards"><ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />Cards</ToggleButton>
          <ToggleButton value="table"><TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />Tabelle</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <PmCard>
        <Stack spacing={2}>
          <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} variant="scrollable">
            <Tab value="ls" label={searchActive ? `Liberating Structures (${counts.ls})` : 'Liberating Structures'} />
            <Tab value="pip" label={searchActive ? `PIP DECKS (${counts.pip})` : 'PIP DECKS'} />
            <Tab value="org" label={searchActive ? `Organisationsformen (${counts.org})` : 'Organisationsformen'} />
            <Tab value="own" label={searchActive ? `Eigene Methoden (${counts.own})` : 'Eigene Methoden'} />
          </Tabs>

          <TextField
            size="small"
            fullWidth
            placeholder="Methode suchen (Titel, Beschreibung, URL)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {searchActive && counts[tab] === 0 && totalMatches > 0 && (
            <PmInfoBox variant="info">
              Keine Treffer in diesem Tab. Insgesamt {totalMatches} Treffer in anderen Tabs (siehe Zahlen in den Tab-Labels).
            </PmInfoBox>
          )}

          {tabContent}
        </Stack>
      </PmCard>

      {/* Eigene Methode anlegen / bearbeiten */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{editId ? 'Methode bearbeiten' : 'Neue Methode'}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              required
              label="Titel"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Beschreibung"
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Link"
              placeholder="https://…"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              fullWidth
              type="url"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        title="Methode löschen?"
        message="Dieser Eintrag wird endgültig aus dem lokalen Speicher entfernt."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
